import { subscribeUserUiPreferences } from "../lib/userUiPreferencesSync.js";
import { onPreferencesLeave } from "../utils/preferencesDelegation.js";
import React from "react";
import {
  fetchBillingPreferences,
  putBillingPreferences,
} from "../api/billingPreferencesApi.js";
import {
  createDefaultBillingPreferences,
  normalizeBillingPreferences,
} from "../utils/billingPreferences.js";

export const BILLING_PREFERENCES_SAVE_DEBOUNCE_MS = 500;

function samePreferences(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

export default function useBillingPreferences({
  debounceMs = BILLING_PREFERENCES_SAVE_DEBOUNCE_MS,
  loadPreferences = fetchBillingPreferences,
  onError,
  savePreferences = putBillingPreferences,
} = {}) {
  const [snapshot, setSnapshot] = React.useState({
    isLoading: true,
    isSaving: false,
    preferences: null,
  });
  const confirmedRef = React.useRef(null);
  const currentRef = React.useRef(null);
  const disposedRef = React.useRef(false);
  const isLoadingRef = React.useRef(true);
  const isSavingRef = React.useRef(false);
  const loadAbortControllerRef = React.useRef(null);
  const onErrorRef = React.useRef(onError);
  const pendingRef = React.useRef(null);
  const savePromiseRef = React.useRef(null);
  const saveTimerRef = React.useRef(null);
  const flushRef = React.useRef(null);
  onErrorRef.current = onError;

  const publish = React.useCallback(() => {
    if (disposedRef.current) return;
    setSnapshot({
      isLoading: isLoadingRef.current,
      isSaving: isSavingRef.current || pendingRef.current !== null,
      preferences: currentRef.current,
    });
  }, []);

  React.useEffect(() => subscribeUserUiPreferences("billing", (incoming) => {
    if (disposedRef.current || isLoadingRef.current || isSavingRef.current || pendingRef.current) return false;
    const next = normalizeBillingPreferences(incoming ?? createDefaultBillingPreferences());
    // Conserver l'onglet ouvert pendant que l'utilisateur travaille.
    next.lastTab = currentRef.current.lastTab;
    if (!samePreferences(currentRef.current, next)) {
      confirmedRef.current = currentRef.current = next;
      publish();
    }
    return true;
  }), [publish]);

  React.useEffect(() => onPreferencesLeave(async function flushAll() {
    await flushRef.current?.();
    if (pendingRef.current) return flushAll();
  }), []);

  const clearSaveTimer = React.useCallback(() => {
    if (saveTimerRef.current !== null) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  }, []);

  const flush = React.useCallback(async () => {
    clearSaveTimer();
    if (disposedRef.current) return currentRef.current;
    if (savePromiseRef.current) {
      await savePromiseRef.current.catch(() => undefined);
      return pendingRef.current ? flushRef.current?.() : currentRef.current;
    }
    if (!pendingRef.current) return currentRef.current;

    const payload = pendingRef.current;
    pendingRef.current = null;
    isSavingRef.current = true;
    publish();

    const savePromise = Promise.resolve().then(() => savePreferences(payload));
    savePromiseRef.current = savePromise;
    try {
      const saved = normalizeBillingPreferences(await savePromise);
      confirmedRef.current = saved;
      if (!pendingRef.current) currentRef.current = saved;
      return currentRef.current;
    } catch (error) {
      if (!disposedRef.current) {
        if (!pendingRef.current) currentRef.current = confirmedRef.current;
        onErrorRef.current?.(error instanceof Error ? error : new Error("Impossible d’enregistrer les préférences de facturation."), {
          operation: "save",
        });
      }
      throw error;
    } finally {
      if (savePromiseRef.current === savePromise) savePromiseRef.current = null;
      isSavingRef.current = false;
      if (!disposedRef.current) {
        publish();
        if (pendingRef.current) {
          saveTimerRef.current = setTimeout(() => {
            saveTimerRef.current = null;
            void flushRef.current?.().catch(() => {});
          }, Math.max(0, debounceMs));
        }
      }
    }
  }, [clearSaveTimer, debounceMs, publish, savePreferences]);
  flushRef.current = flush;

  const scheduleSave = React.useCallback(() => {
    clearSaveTimer();
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void flushRef.current?.().catch(() => {});
    }, Math.max(0, debounceMs));
  }, [clearSaveTimer, debounceMs]);

  const updatePreferences = React.useCallback((updater, { immediate = false } = {}) => {
    if (!currentRef.current) return immediate ? Promise.resolve(null) : null;
    const candidate = typeof updater === "function" ? updater(currentRef.current) : updater;
    const next = normalizeBillingPreferences(candidate);
    if (samePreferences(currentRef.current, next)) {
      return immediate ? Promise.resolve(currentRef.current) : currentRef.current;
    }
    currentRef.current = next;
    pendingRef.current = next;
    publish();
    if (immediate) return flush();
    scheduleSave();
    return next;
  }, [flush, publish, scheduleSave]);

  React.useEffect(() => {
    disposedRef.current = false;
    const abortController = new AbortController();
    loadAbortControllerRef.current = abortController;
    isLoadingRef.current = true;
    publish();

    void loadPreferences({ signal: abortController.signal })
      .then((loaded) => {
        if (disposedRef.current || abortController.signal.aborted) return;
        const preferences = normalizeBillingPreferences(loaded ?? createDefaultBillingPreferences());
        confirmedRef.current = preferences;
        currentRef.current = preferences;
        isLoadingRef.current = false;
        publish();
        if (loaded === null) {
          pendingRef.current = preferences;
          publish();
          void flushRef.current?.().catch(() => {});
        }
      })
      .catch((error) => {
        if (disposedRef.current || abortController.signal.aborted) return;
        const preferences = createDefaultBillingPreferences();
        confirmedRef.current = preferences;
        currentRef.current = preferences;
        isLoadingRef.current = false;
        publish();
        onErrorRef.current?.(error instanceof Error ? error : new Error("Impossible de charger les préférences de facturation."), {
          operation: "load",
        });
      });

    return () => {
      const pending = pendingRef.current;
      clearSaveTimer();
      abortController.abort();
      loadAbortControllerRef.current = null;
      if (pending) void savePreferences(pending, { keepalive: true }).catch(() => {});
      disposedRef.current = true;
    };
  }, [clearSaveTimer, loadPreferences, publish, savePreferences]);

  return {
    ...snapshot,
    flush,
    updatePreferences,
  };
}
